import { PASSWORD_MIN_LENGTH } from "../constants";
import { ensureHTTPS } from "./utils";

export function isValidEmail(email) {
  if (typeof email !== 'string') {
    return false;
  }
  
  // Regular expression for basic email validation.  This isn't perfect,
  // but it's a good starting point and catches most common errors.  For truly
  // robust validation, you might consider a dedicated email validation library.
  const emailRegex = /^[^\s@]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);

}

export function isValidURL(urlString) {
  if (typeof urlString !== 'string') {
    return false;
  }

  // More comprehensive regex for URL validation. Allows trailing slash, ?, ?& and #
  const urlRegex = /^(https?:\/\/)?([\w.-]+(?:\.[\w.-]+)+)(:\d+)?(\/[-a-zA-Z0-9_.%]*)*(\?[;&a-zA-Z0-9=_.-]*)?(#[-\w]*)?$/;

  if (!urlRegex.test(urlString)) {
    return false;
  }

  try {
    new URL(ensureHTTPS(urlString));
    return true;
  }
  catch {
    return false;
  }
}

/**
 * Validates a YouTube search term.
 * If the term is not a YouTube URL, it is considered a valid keyword search.
 * If the term appears to be a YouTube URL, it validates its structure.
 * @param {string} searchTerm The search term to validate.
 * @returns {boolean} True if the search term is valid, false otherwise.
 */
export function isValidYoutubeSearch(searchTerm) {
  if (!searchTerm || typeof searchTerm !== 'string') {
      return true;
  }

  const term = searchTerm.trim();

  if (!term.includes('youtube.com') && !term.includes('youtu.be')) {
      return true;
  }

  // If it looks like a URL, validate its structure more strictly.
  // This regex checks for common valid YouTube video URL patterns.
  // - Optional http(s):// and www.
  // - Supports youtube.com/watch?v=... and youtu.be/... formats
  // - Requires a valid 11-character video ID
  // - Allows for optional trailing parameters (like timestamps)
  const youtubeUrlRegex = /^(https?:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtu\.be\/)[a-zA-Z0-9_-]{11}(\S*)?$/;

  return youtubeUrlRegex.test(term);
}

export function isValidSSN(ssn) {
  const ssnPattern = /^(?!000|666)[0-8]\d{2}-(?!00)\d{2}-(?!0000)\d{4}$/;

  return ssnPattern.test(ssn);
}

export function isValidZIPCode(zipCode) {
  const zipPattern = /^\d{5}(-\d{4})?$/;

  return zipPattern.test(zipCode);
}

export function isValidPhone(phone) {
  // Optional leading +, then 7 to 20 digits with spaces, brackets, dots and
  // dashes allowed as separators.
  const phonePattern = /^\+?[0-9][0-9\s().-]{6,19}$/;

  return phonePattern.test(phone);
}

/**
 * checks if the given password is as per the password rules. i.e.
 * 
 * Password must be at least 8 characters long, it must contain at least one
 * lower case, one upper case english alphbet, one digit and one special
 * character out of
 * 
 * `< > ~ `` ! @ # $ % ^ & * ( ) _ + { } [ ] | " ' : ;`
 * 
 * It must not start and end with a white space.
 * @param {string} password 
 * @returns `true` if the given password is as per the password rules. `false`
 * otherwise.
 */
export function isValidPassword(password) {
  if(!password) return false

  if (password.length < PASSWORD_MIN_LENGTH) return false;

  if (password.startsWith(' ') || password.endsWith(' ')) return false;

  let metCount = 1;
  if (/[a-z]/.test(password)) metCount++;
  if (/[A-Z]/.test(password)) metCount++;
  if (/\d/.test(password)) metCount++;
  if (/[<>~`!@#$%^&*()_+{}[\]|"':;]/.test(password)) metCount++;

  // Accept "fair" or "strong" passwords (3+ out of 5 criteria met)
  return metCount >= 3;
}

/**
 * Validates the given `fieldValue` against the given `validationRules` that has
 * the following structure with all the attributes as optional.
 * 
 *`{`

 *`   isRequired: boolean,`
 * 
 *`   minLength: number, // if the value is a string`
 * 
 *`   maxLength: number, // if the value is a string`
 * 
 *`   minValue: number, // if the value is a number`
 * 
 *`   maxValue: number, // if the value is a number`
 * 
 *`   isURL: boolean,`
 * 
 *`   isEamil: boolean`
 * 
 *`   isSSN: boolean,`
 * 
 *`   isZIPCode: boolean,`
 * 
 *`   isPassword: boolean`
 * 
 *`}`
 * 
 * @param {*} validationRules An object containing the validation rules as
 * specified above.
 * @param {*} fieldValue The value to be validated.
 * @param {*} fieldLabel The label of the field to be used in the error message.
 * @returns A string containing the error message if the field is invalid, or `null`
 * if the field is valid.
 */
export function validateField(validationRules, fieldValue, fieldLabel) {
  if (!validationRules?.isRequired && !fieldValue) {
    return null;
  }

  if (validationRules?.isRequired && fieldValue !== false && !fieldValue) {
    return `${fieldLabel} is required.`;
  }

  if (validationRules?.minLength && fieldValue?.length < validationRules.minLength) {
    return `${fieldLabel} must be at least ${validationRules.minLength} characters long.`;
  }

  if (validationRules?.maxLength && fieldValue?.length > validationRules.maxLength) {
    return `${fieldLabel} must be at most ${validationRules.maxLength} characters long.`;
  }

  if (validationRules?.minValue && fieldValue < validationRules.minValue) {
    return `${fieldLabel} must be at least ${validationRules.minValue}.`;
  }

  if (validationRules?.maxValue && fieldValue > validationRules.maxValue) {
    return `${fieldLabel} must be at most ${validationRules.maxValue}.`;
  }

  if (validationRules?.isURL && !isValidURL(fieldValue)) {
    return `${fieldLabel} is not a valid URL.`;
  }

  if (validationRules?.isEmail && !isValidEmail(fieldValue)) {
    return `${fieldLabel} is not a valid email address.`;
  }

  if (validationRules?.isSSN && !isValidSSN(fieldValue)) {
    return `${fieldLabel} is not a valid SSN.`;
  }

  if (validationRules?.isZIPCode && !isValidZIPCode(fieldValue)) {
    return `${fieldLabel} is not a valid ZIP code.`;
  }

  if (validationRules?.isPassword && !isValidPassword(fieldValue)) {
    return `${fieldLabel} must be at least 8 characters and meet at least 2 of: uppercase letter, lowercase letter, a digit, special character.`;
  }

  if(validationRules?.isYoutubeSearch && !isValidYoutubeSearch(fieldValue)) {
    return `${fieldLabel} is not a valid Youtube URL or search term.`;
  }

  return null;
}